import React, { useEffect, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from '../../components/ui/button'
import { getLocalApiUrl, authFetch } from '../../config/api'
import FormFillFields, { FormLocaleSwitch } from './FormFillFields'
import FormBrandingHeader from './FormBrandingHeader'
import { DEFAULT_LOCALE, normalizeLocales, resolveFormDisplayTitle } from './formBuilderModel'

function fmtWhen(v) {
  if (!v) return '—'
  try {
    return new Date(v).toLocaleString('en-AU')
  } catch {
    return String(v)
  }
}

export default function FormResponseViewModal({ assignmentId, open, onClose }) {
  const [bundle, setBundle] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [locale, setLocale] = useState(DEFAULT_LOCALE)

  useEffect(() => {
    if (!open || !assignmentId) {
      setBundle(null)
      setError('')
      return undefined
    }
    let cancelled = false
    setLoading(true)
    setError('')
    authFetch(getLocalApiUrl(`/api/forms/assignments/${assignmentId}`))
      .then((r) => r.json())
      .then((j) => {
        if (cancelled) return
        if (!j.success) throw new Error(j.error || 'Failed to load response')
        setBundle(j.data)
        const locales = normalizeLocales(j.data?.version?.settings)
        setLocale(j.data?.submission?.resolvedSnapshot?.locale || locales[0] || DEFAULT_LOCALE)
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || 'Failed to load response')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [open, assignmentId])

  if (!open) return null

  const a = bundle?.assignment
  const settings = bundle?.version?.settings
  const answers = bundle?.submission?.answers || {}
  const { schemaVersion, ...answerFields } = answers

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-3 sm:p-6">
      <div className="absolute inset-0 bg-black/45" onClick={onClose} role="presentation" />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col border border-slate-200">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100 shrink-0">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Form response</div>
            <h2 className="text-lg font-bold text-[#073B4D] m-0 mt-0.5 truncate">
              {a?.formName || 'Form'}
            </h2>
            <p className="text-xs text-slate-500 m-0 mt-1">
              {bundle?.submission?.ref ? `Ref ${bundle.submission.ref} · ` : ''}
              Submitted {fmtWhen(bundle?.submission?.submittedAt)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 shrink-0"
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {error ? <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div> : null}
          {loading ? <div className="text-sm text-slate-500">Loading response…</div> : null}
          {!loading && bundle?.submission && bundle?.version ? (
            <>
              <FormLocaleSwitch settings={settings} locale={locale} onChange={setLocale} className="mb-4" />
              <FormBrandingHeader formName={resolveFormDisplayTitle(settings, a?.formName, locale)} />
              <FormFillFields
                definition={bundle.version.definition}
                settings={settings}
                locale={locale}
                answers={answerFields}
                readOnly
                onChange={() => {}}
              />
            </>
          ) : null}
          {!loading && !error && bundle && !bundle.submission ? (
            <div className="text-sm text-slate-500">No submission on this assignment yet.</div>
          ) : null}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 flex justify-end shrink-0 bg-slate-50/80 rounded-b-xl">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  )
}
